
import React, { useEffect, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, IconButton, MenuItem, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography
} from '@mui/material';
import {
  Add, Download, Search, VisibilityOutlined, FilterAltOutlined
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { apiFetch, pobierzExcel } from '../services/api';
import { useLogowanie } from '../context/KontekstLogowania';

const statusStyles = {
  AKTYWNY:{bg:'#e7f7ed',fg:'#21833d'},
  W_SERWISIE:{bg:'#fff4dc',fg:'#b66b00'},
  USZKODZONY:{bg:'#ffe8e8',fg:'#b3261e'},
  WYCOFANY:{bg:'#edf1f6',fg:'#586779'},
  MAGAZYN:{bg:'#eaf2ff',fg:'#1f5fbf'},
  NOWY:{bg:'#eef0ff',fg:'#4f46b8'},
  SPRZEDANY:{bg:'#f2ecff',fg:'#6d43b7'},
  ZLIKWIDOWANY:{bg:'#f3f4f6',fg:'#4b5563'}
};

const columns = [
  {key:'numer_unikatowy',label:'Nr unikatowy'},
  {key:'kategoria',label:'Kategoria'},
  {key:'producent',label:'Producent'},
  {key:'model',label:'Model'},
  {key:'numer_inw',label:'Numer INW'},
  {key:'numer_seryjny',label:'Numer seryjny'},
  {key:'lokalizacja',label:'Lokalizacja'},
  {key:'uzytkownik',label:'Użytkownik'},
  {key:'status',label:'Status'},
  {key:'data_przyjecia',label:'Data przyjęcia'}
];

function fmtDate(v){
  if(!v) return '—';
  const d=new Date(v);
  return Number.isNaN(d.getTime()) ? String(v).slice(0,10) : d.toLocaleDateString('pl-PL');
}

export default function SrodkiTrwale() {
  const { maUprawnienie } = useLogowanie();
  const [wiersze, setWiersze] = useState([]);
  const [lokalizacje, setLokalizacje] = useState([]);
  const [kategorie, setKategorie] = useState([]);
  const [pracownicy, setPracownicy] = useState([]);
  const [filtry, setFiltry] = useState({ search:'', lokalizacja:'', kategoria:'', status:'', uzytkownik:'' });
  const [sort, setSort] = useState({key:'numer_unikatowy',order:'desc'});
  const [strona, setStrona] = useState({ page:1, pages:1, total:0, limit:20 });
  const [zaznaczone,setZaznaczone]=useState([]);

  const zaladuj = async (page=1, currentSort=sort) => {
    const q = new URLSearchParams({ page, limit:20, sort:currentSort.key, order:currentSort.order });
    Object.entries(filtry).forEach(([k,v])=>{if(v) q.set(k,v);});
    const data = await apiFetch(`/srodki-trwale?${q}`);
    setWiersze(data.dane);
    setStrona(data.paginacja);
    setZaznaczone([]);
  };

  useEffect(() => {
    Promise.all([
      apiFetch('/slowniki/lokalizacje'),
      apiFetch('/slowniki/kategorie'),
      apiFetch('/slowniki/pracownicy')
    ]).then(([l,k,p]) => { setLokalizacje(l); setKategorie(k); setPracownicy(p); });
    zaladuj(1,sort);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const zmien = pole => e => setFiltry(f=>({...f,[pole]:e.target.value}));
  const sortuj = key => {
    const next={key,order:sort.key===key && sort.order==='asc' ? 'desc' : 'asc'};
    setSort(next);
    zaladuj(1,next);
  };
  const allChecked=wiersze.length>0 && zaznaczone.length===wiersze.length;

  return <Box>
    <Paper sx={{borderRadius:3,border:'1px solid #e3eaf3',boxShadow:'0 8px 26px rgba(18,46,84,.055)',overflow:'hidden'}}>
      <Box sx={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:2,p:2.5,borderBottom:'1px solid #edf1f6',flexWrap:'wrap'}}>
        <Box>
          <Typography variant="h4" fontWeight={900}>Środki trwałe</Typography>
          <Typography color="text.secondary">Przeglądaj, wyszukuj i zarządzaj środkami trwałymi w firmie.</Typography>
        </Box>
        <Box sx={{display:'flex',gap:1}}>
          <Button
            variant="outlined"
            startIcon={<Download/>}
            disabled={!maUprawnienie('REPORT_EXPORT')}
            onClick={()=>pobierzExcel('/raporty/srodki.xlsx','srodki_trwale.xlsx')}
          >
            Eksportuj
          </Button>
          {maUprawnienie('ASSET_CREATE') ? (
            <Button component={Link} to="/srodki-trwale/dodaj" variant="contained" startIcon={<Add/>}>Dodaj środek trwały</Button>
          ) : (
            <Tooltip title="Ta funkcja nie jest dostępna dla Twojej roli." arrow>
              <span>
                <Button disabled variant="contained" startIcon={<Add/>}>Dodaj środek trwały</Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box sx={{p:2,bgcolor:'#fbfdff',borderBottom:'1px solid #edf1f6'}}>
        <Box sx={{display:'grid',gridTemplateColumns:{xs:'1fr',xl:'2fr 1fr 1fr 1fr 1fr auto'},gap:1.2}}>
          <TextField size="small" placeholder="Wyszukaj: model, producent, numer INW, SN..." value={filtry.search} onChange={zmien('search')}
            InputProps={{startAdornment:<Search sx={{mr:1,color:'#7b8797'}} fontSize="small"/>}}/>
          <TextField size="small" select label="Kategoria" value={filtry.kategoria} onChange={zmien('kategoria')}>
            <MenuItem value="">Wszystkie</MenuItem>{kategorie.map(x=><MenuItem key={x.id_kategorii} value={x.id_kategorii}>{x.nazwa}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Status" value={filtry.status} onChange={zmien('status')}>
            <MenuItem value="">Wszystkie</MenuItem>
            {['NOWY','AKTYWNY','MAGAZYN','W_SERWISIE','USZKODZONY','WYCOFANY','SPRZEDANY','ZLIKWIDOWANY'].map(x=><MenuItem key={x} value={x}>{x}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Lokalizacja" value={filtry.lokalizacja} onChange={zmien('lokalizacja')}>
            <MenuItem value="">Wszystkie</MenuItem>{lokalizacje.map(x=><MenuItem key={x.id_lokalizacji} value={x.id_lokalizacji}>{x.nazwa}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Użytkownik" value={filtry.uzytkownik} onChange={zmien('uzytkownik')}>
            <MenuItem value="">Wszyscy</MenuItem>{pracownicy.map(x=><MenuItem key={x.id_pracownika} value={x.id_pracownika}>{x.imie} {x.nazwisko}</MenuItem>)}
          </TextField>
          <Button variant="outlined" startIcon={<FilterAltOutlined/>} onClick={()=>zaladuj(1)}>Filtruj</Button>
        </Box>
      </Box>

      <Box sx={{overflowX:'auto'}}>
        <Table size="small" sx={{minWidth:1280}}>
          <TableHead>
            <TableRow sx={{bgcolor:'#f8fafc'}}>
              <TableCell padding="checkbox">
                <Checkbox size="small" checked={allChecked}
                  indeterminate={zaznaczone.length>0 && !allChecked}
                  onChange={e=>setZaznaczone(e.target.checked?wiersze.map(x=>x.id_srodka):[])}/>
              </TableCell>
              {columns.map(c=><TableCell key={c.key} sx={{fontWeight:800,color:'#344054',whiteSpace:'nowrap'}}>
                <TableSortLabel
                  active={sort.key===c.key}
                  direction={sort.key===c.key?sort.order:'asc'}
                  onClick={()=>sortuj(c.key)}
                >
                  {c.label}
                </TableSortLabel>
              </TableCell>)}
              <TableCell sx={{fontWeight:800,color:'#344054'}}>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {wiersze.map(r=>{
              const st=statusStyles[r.status] || {bg:'#eef2f6',fg:'#475569'};
              return <TableRow key={r.id_srodka} hover sx={{'& td':{borderColor:'#edf1f6'}}}>
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={zaznaczone.includes(r.id_srodka)}
                    onChange={e=>setZaznaczone(z=>e.target.checked?[...z,r.id_srodka]:z.filter(id=>id!==r.id_srodka))}/>
                </TableCell>
                <TableCell sx={{fontWeight:900,color:'#1663d8'}}>{r.numer_unikatowy || '—'}</TableCell>
                <TableCell>{r.kategoria || '—'}</TableCell>
                <TableCell>{r.producent || '—'}</TableCell>
                <TableCell sx={{fontWeight:650}}>{r.model || '—'}</TableCell>
                <TableCell>{r.numer_inw || '—'}</TableCell>
                <TableCell>{r.numer_seryjny || '—'}</TableCell>
                <TableCell>{r.lokalizacja || '—'}</TableCell>
                <TableCell>{r.uzytkownik || '—'}</TableCell>
                <TableCell><Chip size="small" label={r.status} sx={{bgcolor:st.bg,color:st.fg,fontWeight:850,fontSize:10}}/></TableCell>
                <TableCell>{fmtDate(r.data_przyjecia)}</TableCell>
                <TableCell>
                  <Tooltip title="Pokaż szczegóły">
                    <IconButton component={Link} to={`/srodki-trwale/${r.id_srodka}`} size="small"><VisibilityOutlined fontSize="small"/></IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>;
            })}
          </TableBody>
        </Table>
      </Box>

      <Box sx={{p:2,display:'flex',justifyContent:'space-between',alignItems:'center',borderTop:'1px solid #edf1f6',flexWrap:'wrap',gap:1}}>
        <Typography variant="body2" color="text.secondary">
          Wyświetlanie {strona.total ? (strona.page-1)*strona.limit+1 : 0}–{Math.min(strona.page*strona.limit,strona.total)} z {strona.total} wyników
          {zaznaczone.length ? ` · zaznaczono: ${zaznaczone.length}` : ''}
        </Typography>
        <Box sx={{display:'flex',alignItems:'center',gap:.7}}>
          <Button size="small" variant="outlined" disabled={strona.page<=1} onClick={()=>zaladuj(strona.page-1)}>‹</Button>
          <Box sx={{minWidth:34,height:32,borderRadius:1.5,bgcolor:'#2364d8',color:'#fff',display:'grid',placeItems:'center',fontWeight:800}}>{strona.page}</Box>
          <Typography variant="body2" color="text.secondary">z {Math.max(1,strona.pages)}</Typography>
          <Button size="small" variant="outlined" disabled={strona.page>=strona.pages} onClick={()=>zaladuj(strona.page+1)}>›</Button>
          <Box sx={{px:1.2,py:.6,border:'1px solid #dbe3ec',borderRadius:1.5,fontSize:13}}>20 / strona</Box>
        </Box>
      </Box>
    </Paper>
  </Box>;
}
